/**
 * Per-signal identifying-bit estimates.
 *
 * These are ROUGH. There is no server-side population to compare against, so every number here is
 * an average entropy for the attribute taken from (or nudged from) published studies, not the
 * rarity of *your* particular value. Where a figure is our own guess it is marked `estimate` and
 * a `TODO: source` comment. Prefer conservative guesses over invented precision.
 *
 * Bump ESTIMATE_METHOD_VERSION whenever a number, group, cap, weight or the combination method in
 * score.ts changes, so exported reports stay comparable.
 */

export const ESTIMATE_METHOD_VERSION = '1.0';

export type SourceId =
  | 'panopticlick'
  | 'amiunique'
  | 'hiding-crowd'
  | 'survey'
  | 'cover-your-tracks'
  | 'fpjs'
  | 'browserleaks'
  | 'englehardt'
  | 'cao'
  | 'mowery'
  | 'estimate';

export const SOURCES: Record<SourceId, { citation: string; url?: string }> = {
  panopticlick: {
    citation: 'Eckersley, P. “How Unique Is Your Web Browser?” PETS 2010 (EFF Panopticlick).',
    url: 'https://coveryourtracks.eff.org/static/browser-uniqueness.pdf',
  },
  amiunique: {
    citation:
      'Laperdrix, P., Rudametkin, W., Baudry, B. “Beauty and the Beast: Diverting modern web browsers to build unique browser fingerprints.” IEEE S&P 2016 (AmIUnique).',
    url: 'https://amiunique.org/',
  },
  'hiding-crowd': {
    citation:
      'Gómez-Boix, A., Laperdrix, P., Baudry, B. “Hiding in the Crowd: an Analysis of the Effectiveness of Browser Fingerprinting at Large Scale.” WWW 2018.',
  },
  survey: {
    citation: 'Laperdrix, P., Bielova, N., Baudry, B., Avoine, G. “Browser Fingerprinting: A Survey.” ACM TWEB 2020.',
  },
  'cover-your-tracks': {
    citation: 'EFF Cover Your Tracks: per-attribute bits shown to visitors.',
    url: 'https://coveryourtracks.eff.org/',
  },
  fpjs: {
    citation: 'FingerprintJS open-source library and technical write-ups (audio, math, fonts, canvas).',
    url: 'https://github.com/fingerprintjs/fingerprintjs',
  },
  browserleaks: {
    citation: 'BrowserLeaks write-ups (WebRTC, canvas, WebGL, fonts).',
    url: 'https://browserleaks.com/',
  },
  englehardt: {
    citation:
      'Englehardt, S., Narayanan, A. “Online Tracking: A 1-million-site Measurement and Analysis.” ACM CCS 2016 (audio and WebRTC fingerprinting in the wild).',
  },
  cao: {
    citation: 'Cao, Y., Li, S., Wijmans, E. “(Cross-)Browser Fingerprinting via OS and Hardware Level Features.” NDSS 2017.',
  },
  mowery: {
    citation: 'Mowery, K., Shacham, H. “Pixel Perfect: Fingerprinting Canvas in HTML5.” W2SP 2012.',
  },
  estimate: {
    citation: 'Conservative estimate by this project; no directly comparable published figure.',
  },
};

/**
 * Correlation groups. Signals in the same group largely re-describe the same underlying fact
 * (e.g. browser build, GPU, installed software), so score.ts discounts them against each other.
 *
 * - `cap`: most bits the group can contribute before weighting.
 * - `weight`: how much the group adds beyond what the browser/OS group already implies.
 *   Groups that are mostly predictable from the OS and browser (fonts, graphics, audio, media)
 *   get lower weights.
 */
export type GroupId =
  | 'browser'
  | 'graphics'
  | 'fonts'
  | 'display'
  | 'hardware'
  | 'locale'
  | 'audio'
  | 'media'
  | 'network'
  | 'config'
  | 'misc';

export interface GroupInfo {
  label: string;
  cap: number;
  weight: number;
  why: string;
}

export const GROUPS: Record<GroupId, GroupInfo> = {
  browser: {
    label: 'Browser & OS build',
    cap: 10,
    weight: 1,
    why: 'User agent, platform and engine quirks all describe the same browser version and OS.',
  },
  graphics: {
    label: 'Graphics rendering',
    cap: 10,
    weight: 0.5,
    why: 'Canvas and WebGL output depend on GPU, driver and OS together; much of it follows from the OS.',
  },
  fonts: {
    label: 'Installed fonts',
    cap: 10,
    weight: 0.7,
    why: 'Default OS fonts follow from the OS; fonts you installed yourself are what make the list distinctive.',
  },
  display: {
    label: 'Screen & display',
    cap: 7,
    weight: 0.5,
    why: 'Resolution, available area, pixel ratio and color capabilities describe one physical display.',
  },
  hardware: { label: 'Hardware', cap: 4, weight: 0.5, why: 'CPU cores, memory and input devices are coarse and partly bucketed.' },
  locale: {
    label: 'Language & time zone',
    cap: 7,
    weight: 0.7,
    why: 'Languages, time zone and regional formats describe where you are and how you configured your system.',
  },
  audio: { label: 'Audio stack', cap: 4, weight: 0.4, why: 'Audio processing output mostly follows from OS and browser version.' },
  media: {
    label: 'Media & speech',
    cap: 5,
    weight: 0.4,
    why: 'Codecs, devices and speech voices are largely set by the OS and installed language packs.',
  },
  network: { label: 'Network (WebRTC)', cap: 6, weight: 1, why: 'Local network addresses are independent of the rest of the fingerprint.' },
  config: {
    label: 'Preferences & privacy settings',
    cap: 5,
    weight: 0.4,
    why: 'Each setting is individually common; together they add a little.',
  },
  misc: { label: 'Other', cap: 2, weight: 0.5, why: 'Timer precision and similar engine details.' },
};

/** Within a group, the strongest signal counts fully; every other signal counts this fraction. */
export const IN_GROUP_DECAY = 0.25;

/**
 * log2(world internet users) is roughly 32.5 bits. Beyond ~33 bits a fingerprint is already
 * enough to single out one browser among everyone online, so more bits carry no extra meaning.
 */
export const MAX_TOTAL_BITS = 33;

/** Bits charged when an API is missing or blocked: being blocked is a (common) signal in itself. */
export const DEFAULT_BLOCKED_BITS = 0.3;

export interface Estimate {
  /** Average identifying bits for this attribute, or null for informational rows that are not scored. */
  bits: number | null;
  group: GroupId;
  /** Included in the stable fingerprint hash (does not change between visits on its own). */
  stable: boolean;
  /** Bits when the API is unavailable. Defaults to DEFAULT_BLOCKED_BITS. */
  blockedBits?: number;
  sources: SourceId[];
}

const e = (bits: number | null, group: GroupId, stable: boolean, sources: SourceId[], blockedBits?: number): Estimate => ({
  bits,
  group,
  stable,
  sources,
  ...(blockedBits === undefined ? {} : { blockedBits }),
});

export const ESTIMATES: Record<string, Estimate> = {
  // ── Navigator ────────────────────────────────────────────────────────────────────────────────
  // AmIUnique 2016: 9.78 bits; Panopticlick 2010: 10.0. Lowered because Chromium's User-Agent
  // Reduction (2022–23) froze the OS version and minor version in the UA string.
  // TODO: source for a post-reduction measurement.
  'nav.userAgent': e(7, 'browser', true, ['amiunique', 'panopticlick']),
  // Low-entropy client hints repeat the UA. TODO: source.
  'nav.uaData': e(0.5, 'browser', true, ['estimate']),
  // High-entropy hints restore what UA reduction removed (full version, platform version, model). TODO: source.
  'nav.uaDataHigh': e(3, 'browser', true, ['estimate']),
  'nav.platform': e(2.3, 'browser', true, ['amiunique']), // AmIUnique 2016: 2.31 bits
  'nav.vendor': e(0.5, 'browser', true, ['estimate']),
  'nav.product': e(0.1, 'browser', true, ['estimate']),
  'nav.webdriver': e(0.1, 'browser', true, ['estimate']),
  'nav.pdfViewerEnabled': e(0.3, 'browser', true, ['estimate']),
  // AmIUnique 2016: 11.06 bits, but browsers now return a fixed, frozen plugin list (or none).
  'nav.plugins': e(0.5, 'browser', true, ['amiunique', 'estimate']),
  'nav.mimeTypes': e(0.3, 'browser', true, ['estimate']),
  'nav.vendorGlobals': e(1, 'browser', true, ['fpjs', 'estimate']),
  'nav.doNotTrack': e(0.9, 'config', true, ['amiunique']), // AmIUnique 2016: 0.94 bits
  'nav.globalPrivacyControl': e(0.5, 'config', true, ['estimate']),
  'nav.cookieEnabled': e(0.3, 'config', true, ['panopticlick']), // Panopticlick 2010: 0.353 bits

  // ── Screen ───────────────────────────────────────────────────────────────────────────────────
  // AmIUnique 2016: 4.85 bits (resolution + color depth); Panopticlick 2010: 4.83 bits.
  'screen.size': e(4.8, 'display', true, ['amiunique', 'panopticlick']),
  'screen.avail': e(2, 'display', true, ['estimate']), // TODO: source
  'screen.colorDepth': e(0.5, 'display', true, ['estimate']),
  'screen.dpr': e(1.5, 'display', true, ['cover-your-tracks', 'estimate']), // TODO: source
  'screen.inner': e(null, 'display', false, ['estimate']),
  'screen.outer': e(null, 'display', false, ['estimate']),
  'screen.orientation': e(0.2, 'display', false, ['estimate']),
  'screen.spoofCheck': e(null, 'display', false, ['estimate']),

  // ── Hardware ─────────────────────────────────────────────────────────────────────────────────
  'hw.cores': e(2, 'hardware', true, ['cover-your-tracks', 'estimate']), // TODO: source
  'hw.memory': e(1.5, 'hardware', true, ['cover-your-tracks', 'estimate'], 0.2), // bucketed by Chromium. TODO: source
  'hw.touch': e(1, 'hardware', true, ['estimate']),
  'hw.battery': e(null, 'hardware', false, ['estimate']),
  'hw.connection': e(0.5, 'hardware', false, ['estimate'], 0.1),
  'hw.gamepads': e(null, 'hardware', false, ['estimate']),

  // ── Locale ───────────────────────────────────────────────────────────────────────────────────
  'locale.language': e(1, 'locale', true, ['estimate']),
  // AmIUnique 2016: 5.92 bits for the Accept-Language header. Value-aware: see languagesBits().
  'locale.languages': e(3, 'locale', true, ['amiunique']),
  // AmIUnique 2016: 3.34 bits; Panopticlick 2010: 3.04 bits. Value-aware: see timezoneBits().
  'locale.timezone': e(3.3, 'locale', true, ['amiunique', 'panopticlick']),
  'locale.tzOffset': e(0, 'locale', false, ['estimate']), // fully implied by the time zone; changes with DST
  'locale.intlOptions': e(0.5, 'locale', true, ['estimate']),
  'locale.formats': e(0.5, 'locale', true, ['estimate']),
  'locale.tzMatch': e(null, 'locale', false, ['estimate']),

  // ── Canvas ───────────────────────────────────────────────────────────────────────────────────
  'canvas.hash': e(8.3, 'graphics', true, ['amiunique', 'mowery'], 1), // AmIUnique 2016: 8.28 bits
  'canvas.emoji': e(4, 'graphics', true, ['browserleaks', 'estimate'], 0.2), // TODO: source
  'canvas.randomization': e(null, 'graphics', false, ['estimate']),

  // ── WebGL ────────────────────────────────────────────────────────────────────────────────────
  'webgl.support': e(0.5, 'graphics', true, ['estimate']),
  'webgl.vendor': e(2.1, 'graphics', true, ['amiunique']), // AmIUnique 2016: 2.14 bits
  // AmIUnique 2016: 3.40 bits. Nudged up: modern ANGLE strings embed GPU model and driver API.
  // TODO: source for current figure.
  'webgl.renderer': e(4, 'graphics', true, ['amiunique', 'cao']),
  'webgl.extensions': e(2.5, 'graphics', true, ['browserleaks', 'estimate']), // TODO: source
  'webgl.params': e(2.5, 'graphics', true, ['browserleaks', 'estimate']), // TODO: source
  'webgl.render': e(4, 'graphics', true, ['cao', 'estimate'], 0.2), // TODO: source
  'webgl.randomization': e(null, 'graphics', false, ['estimate']),

  // ── Audio ────────────────────────────────────────────────────────────────────────────────────
  // Englehardt & Narayanan 2016 found it in the wild; later work suggests modest diversity.
  // TODO: source for a bits figure.
  'audio.hash': e(3.5, 'audio', true, ['englehardt', 'fpjs', 'estimate']),
  'audio.sampleRate': e(1, 'audio', true, ['estimate']),
  'audio.baseLatency': e(0.5, 'audio', false, ['estimate']),
  'audio.randomization': e(null, 'audio', false, ['estimate']),

  // ── Fonts ────────────────────────────────────────────────────────────────────────────────────
  // Panopticlick 2010: 13.9 bits; AmIUnique 2016: 8.38 bits (both via full Flash enumeration).
  // JS probing a fixed list sees less. Value-aware: see fontListBits().
  'fonts.list': e(8.4, 'fonts', true, ['panopticlick', 'amiunique']),
  'fonts.osGuess': e(null, 'fonts', false, ['estimate']),
  'fonts.apis': e(0.2, 'fonts', true, ['estimate']),

  // ── Media ────────────────────────────────────────────────────────────────────────────────────
  'media.devices': e(1.5, 'media', true, ['englehardt', 'estimate']), // TODO: source
  'media.codecs': e(1.5, 'media', true, ['estimate']), // TODO: source
  'media.canPlayType': e(1, 'media', true, ['estimate']),
  'media.voices': e(3, 'media', true, ['browserleaks', 'estimate']), // TODO: source

  // ── WebRTC ───────────────────────────────────────────────────────────────────────────────────
  'webrtc.available': e(0.3, 'network', true, ['estimate']),
  // Value-aware: see webrtcBits(). Not in the stable hash because local addresses change with networks.
  'webrtc.localIps': e(0.3, 'network', false, ['englehardt', 'browserleaks']),

  // ── Storage ──────────────────────────────────────────────────────────────────────────────────
  'storage.available': e(0.4, 'config', true, ['amiunique']), // AmIUnique 2016: 0.40 bits (local/session storage)
  'storage.quota': e(2.5, 'config', false, ['estimate']), // TODO: source. Varies with free disk space in some browsers.
  'storage.persisted': e(0.2, 'config', true, ['estimate']),

  // ── Privacy ──────────────────────────────────────────────────────────────────────────────────
  'privacy.adblock': e(1, 'config', true, ['amiunique']), // AmIUnique 2016: 0.99 bits
  'privacy.permissions': e(1, 'config', true, ['estimate']), // TODO: source
  'privacy.trackingHints': e(null, 'config', false, ['estimate']),
  'privacy.antiFingerprinting': e(null, 'config', false, ['estimate']),

  // ── CSS media features ───────────────────────────────────────────────────────────────────────
  'css.colorScheme': e(1, 'config', false, ['estimate']), // can flip with OS auto dark mode
  'css.reducedMotion': e(0.5, 'config', true, ['estimate']),
  'css.reducedTransparency': e(0.3, 'config', true, ['estimate']),
  'css.contrast': e(0.3, 'config', true, ['estimate']),
  'css.forcedColors': e(0.3, 'config', true, ['estimate']),
  'css.invertedColors': e(0.2, 'config', true, ['estimate']),
  'css.input': e(1, 'hardware', true, ['estimate']),
  'css.colorGamut': e(0.8, 'display', true, ['estimate']),
  'css.dynamicRange': e(0.5, 'display', true, ['estimate']),
  'css.displayMode': e(0.1, 'config', true, ['estimate']),
  'css.scrollbar': e(1, 'browser', true, ['estimate']),
  'css.fontSizes': e(1, 'config', true, ['estimate']),

  // ── Math & engine ────────────────────────────────────────────────────────────────────────────
  'math.fp': e(1, 'browser', true, ['fpjs', 'estimate']),
  'math.errorStack': e(0.5, 'browser', true, ['estimate']),
  'math.evalLength': e(0.3, 'browser', true, ['estimate']),
  'math.collator': e(0.8, 'browser', true, ['estimate']),

  // ── Misc ─────────────────────────────────────────────────────────────────────────────────────
  'misc.referrer': e(null, 'misc', false, ['estimate']),
  'misc.focus': e(null, 'misc', false, ['estimate']),
  'misc.keyboard': e(1.5, 'locale', true, ['estimate']), // TODO: source
  'misc.timer': e(1, 'misc', false, ['estimate']),
};

export function estimateFor(id: string): Estimate | undefined {
  return ESTIMATES[id];
}

export function baseBits(id: string): number | null {
  return ESTIMATES[id]?.bits ?? null;
}

export function blockedBits(id: string): number | null {
  const est = ESTIMATES[id];
  if (!est || est.bits === null) return null;
  return est.blockedBits ?? Math.min(DEFAULT_BLOCKED_BITS, est.bits);
}

// ── Value-aware adjustments ──────────────────────────────────────────────────────────────────────

const round1 = (n: number) => Math.round(n * 10) / 10;

/**
 * Fonts beyond the OS defaults are what make a font list distinctive. Each doubling of the number
 * of extra fonts adds about two bits, capped at the group ceiling.
 */
export function fontListBits(extraFonts: number): number {
  return round1(Math.min(GROUPS.fonts.cap, 1.5 + 2 * Math.log2(1 + Math.max(0, extraFonts))));
}

/** One language ("en-US, en" counts as one) is cheap; each extra distinct language narrows the crowd. */
export function languagesBits(count: number): number {
  if (count <= 1) return 2;
  return round1(Math.min(6, 2 + 1.25 * (count - 1)));
}

const UTC_ZONES = new Set(['UTC', 'Etc/UTC', 'Etc/GMT', 'GMT', 'Etc/Universal', 'Etc/Zulu', 'Universal', 'Zulu']);

/** UTC is what Tor Browser and Firefox's resistFingerprinting report, so it is shared by a large crowd. */
export function timezoneBits(timeZone: string | undefined): number {
  if (!timeZone) return blockedBits('locale.timezone') ?? 0.3;
  return UTC_ZONES.has(timeZone) ? 1 : (baseBits('locale.timezone') ?? 3.3);
}

export function isUtcZone(timeZone: string | undefined): boolean {
  return !!timeZone && UTC_ZONES.has(timeZone);
}

export type WebRtcExposure = 'public' | 'private' | 'mdns' | 'none';

export function webrtcBits(exposure: WebRtcExposure): number {
  switch (exposure) {
    case 'public':
      return 6; // a public address identifies your network outright
    case 'private':
      return 4; // TODO: source. Private subnets/hosts are few, but combine strongly with the public IP.
    default:
      return 0.3;
  }
}
